import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';

export interface CachedResource<Result> {
  error$: Observable<Error>;
  pending: boolean;
  refresh$: Subject<void>;
  result$: Observable<Result>;
}

export function createCachedResource<Result>(request$: Observable<Result>): CachedResource<Result> {
  const error$ = new Subject<Error>();
  const resource = {
    pending: false,
    error$: error$.asObservable(),
    refresh$: new Subject<void>(),
    result$: undefined as Observable<Result>,
  };
  resource.result$ = resource.refresh$.pipe(
    startWith(undefined as void),
    switchMap(() => {
      resource.pending = true;
      return request$.pipe(
        tap(() => {
          resource.pending = false;
        }),
        catchError((error) => {
          resource.pending = false;
          error$.next(error);
          return EMPTY;
        }),
      );
    }),
    shareReplay(1),
  );
  return resource;
}

// i.e. Map or WeakMap
interface CacheMap<Key, Value> {
  get(key: Key): Value | undefined;
  has(key: Key): boolean;
  set(key: Key, value: Value): void;
}

/**
 * Boilerplate for computing and retrieving values from a map so that the value object reference remains the same and
 * does not trigger Angular change detection. If the `key` is not in the `map`, invoke `compute` and set the value.
 */
export function getComputedData<Key, Value>(map: CacheMap<Key, Value>, key: Key, compute: (key?: Key) => Value): Value {
  if (!map.has(key)) {
    map.set(key, compute(key));
  }
  return map.get(key);
}
